import { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, TextField } from '@finpay-development/shared-components';
import {
  Paper,
  Grid,
  Typography,
  Button as MUIButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { formatNumberToUSD, calculateDownByPercent, calculateTermPmt, calculateTerm } from 'src/shared/calculators';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../shared/state/root-reducer';
import '../../scss/components/mobile-payments.scss';
import { AppDispatch } from 'src/shared/state/store';
import { setMatchingRule } from 'src/patient/state/patient-slice';
import { patientService } from '../../patient/services/patient-service';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import moment from 'moment';
import { showErrorStatus } from 'src/security/state/user-slice';
import { Program, ProgramRequest } from '@finpay/payment-types';
import axios from 'axios';
import { configSettings } from '../../shared/configuration/config-settings';
import { axiosReadHelper } from '../../shared/service/axios-read-helper';
import { AxiosReadPayload } from '../../shared/service/axios-read-payload';
import { AxiosResultStatus } from '../../shared/service/axios-result-status';

const useStyles = makeStyles((theme) => ({
  fullWidth: {
    width: '100%', // Set width to 100% on mobile
    [theme.breakpoints.up('md')]: {
      width: '80%', // Set width to 80% on screens larger than laptop
    },
    marginTop: '1rem',
    marginBottom: '1rem',
  },
}));

interface PaymentProgramViewProps {
  isMobile?: boolean;
}

const MobilePaymentProgramModal = ({ isMobile = false }: PaymentProgramViewProps) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { patientId: urlPatientId, patientEncounterId: urlEncounterId, programType } = useParams<{ patientId: string; patientEncounterId: string; programType?: string }>();
  const location = useLocation();
  const isCustomResults = location.pathname.includes('/custom-results');

  // Helper function to generate the correct path based on isMobile
  const getProgramPath = useCallback((path: string) => {
    if (isMobile) {
      return path.replace('/hub/patient/', '/mobile/patient/');
    }
    return path;
  }, [isMobile]);
  
  const [customDownPayment, setCustomDownPayment] = useState<string>('');
  const [customTerm, setCustomTerm] = useState<string>('');
  const [customRecurringAmount, setCustomRecurringAmount] = useState<string>('');
  const [fetchedEncounter, setFetchedEncounter] = useState<PatientEncounter | null>(null);
  const [isLoadingEncounter, setIsLoadingEncounter] = useState(false);
  const [isLoadingRule, setIsLoadingRule] = useState(false);
  const [showDownPayment, setShowDownPayment] = useState<boolean>(false);
  const [showRecurringAmount, setShowRecurringAmount] = useState<boolean>(false);
  const [showFields, setShowFields] = useState<boolean>(false);
  const [downPaymentError, setDownPaymentError] = useState<string>('');
  const [recurringAmountError, setRecurringAmountError] = useState<string>('');
  const [isHighRisk, setIsHighRisk] = useState<boolean>(false);
  const [approvalResponse, setApprovalResponse] = useState<string>('');
  const [hreApprovalStatus, setHreApprovalStatus] = useState<string>('');
  const defaultsSetForRouteRef = useRef<string>('');

  const patient = useSelector((state: RootState) => (state?.patientContext?.selectedPatient));
  const patientEncounter = useSelector((state: RootState) => (state?.patientContext?.selectedEncounter));
  const matchingRule = useSelector((state: RootState) => (state?.patientContext?.matchingRule));
  const isLoadingEvaluateRule = useSelector((state: RootState) => (state?.patientContext?.isLoading?.evaluateRule));
  const authToken = useSelector((state: RootState) => state?.userContext?.userProfile?.tokenState?.jwtIdToken);
  const userProfile = useSelector((state: RootState) => state?.userContext?.userProfile);
  
  // Check if user has sys_role_page_action_id 17 (Preauthorized Payment Programs)
  const hasPreauthorizedPermission = useSelector((state: RootState) => {
    const userRolePages = state?.userContext?.userProfile?.userRole?.userRolePages || [];
    return userRolePages.some((page: any) =>
      page?.userRolePageActions?.some((action: any) =>
        action?.sysRolePageAction?.sysRolePageActionId === 99 && action?.isEnabled
      )
    );
  });

  const patientId = urlPatientId ? parseInt(urlPatientId, 10) : 0;
  const encounterId = urlEncounterId ? parseInt(urlEncounterId, 10) : 0;
  
  // Use fetched encounter if available, otherwise fall back to Redux state
  const activeEncounter = fetchedEncounter || patientEncounter;
  const pfrAmt = activeEncounter?.pfrAmt || 0;
  const adjustedPfrAmt = activeEncounter?.adjustedPfrAmt ?? pfrAmt;
  // Calculate paid to date amount: adjustedPfrAmt - pfrCapturedBalance
  const pfrCapturedBalance = activeEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrCapturedBalance ?? adjustedPfrAmt;
  const pfrCollected = adjustedPfrAmt - pfrCapturedBalance;
  const remainingPfr = pfrCapturedBalance;
  // Calculate optimal down payment and recurring amount from rule API response
  const optDownPayment = matchingRule?.optimalDownPmtType === '%'
    ? (matchingRule?.optimalDownPmt || 0) * pfrAmt / 100
    : (matchingRule?.optimalDownPmt || 0);
  const remainingDownPmt = Math.max(0, optDownPayment - pfrCollected);
  const optimalTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
  const recurringAmt = optimalTermNum > 0 && pfrAmt > optDownPayment
    ? (pfrAmt - optDownPayment) / optimalTermNum
    : 0;

  // Determine current program type from URL, default to 'full' if not specified
  // If on custom-results route, treat it as 'custom'
  // If on hre-approval route, treat it as 'custom' for the toggle button selection
  const currentProgramType = isCustomResults ? 'custom' : (programType === 'hre-approval' ? 'custom' : (programType || 'full'));

  // Fetch patient encounter from API
  useEffect(() => {
    (async () => {
      if (patientId > 0 && encounterId > 0) {
        setIsLoadingEncounter(true);
        try {
          const res = await patientService.getPatientInstanceOfCare({
            patientId: patientId,
            encounterId: encounterId,
          });
          if (!res.hasErrors && res.entity) {
            setFetchedEncounter(res.entity);
          } else {
            console.error('Error fetching patient encounter:', res.errorMessage);
          }
        } catch (error) {
          console.error('Error fetching patient encounter:', error);
        } finally {
          setIsLoadingEncounter(false);
        }
      }
    })();
  }, [patientId, encounterId]);

  // Call evaluate rule API with the fetched encounter data
  useEffect(() => {
    (async () => {
      if (patientId > 0 && encounterId > 0 && authToken && fetchedEncounter) {
        setIsLoadingRule(true);
        try {
          const payload: AxiosReadPayload = {
            dataId: 0,
            url: `payment/v2/client/program/rule?clientId=${fetchedEncounter.clientId}&facilityId=${fetchedEncounter.facilityId}&timingRiskId=${fetchedEncounter.timingRisk?.timingRiskId}&payorRiskId=${fetchedEncounter.payorRisk?.payorRiskId}&pfrAmt=${fetchedEncounter.pfrAmt}`,
          };
          
          const matchingRuleRes: AxiosResultStatus = await axiosReadHelper(payload);
          console.log('Evaluate Rule API Response (raw):', matchingRuleRes);
          
          // axiosReadHelper returns AxiosResultStatus with entity property
          // Entity can be an object or an array - handle both cases
          let ruleData: any = null;
          
          if (matchingRuleRes.hasErrors || matchingRuleRes.errorMessage) {
            console.error('Evaluate Rule API Error:', matchingRuleRes.errorMessage || 'Unknown error', matchingRuleRes);
          } else if (matchingRuleRes.entity) {
            // Handle case where entity is an array (take first element) or an object
            if (Array.isArray(matchingRuleRes.entity)) {
              ruleData = matchingRuleRes.entity.length > 0 ? matchingRuleRes.entity[0] : null;
              console.log('Found rule data in entity array (first element):', ruleData);
            } else {
              ruleData = matchingRuleRes.entity;
              console.log('Found rule data in entity object:', ruleData);
            }
          }
          
          if (ruleData && ruleData.ruleId !== undefined && ruleData.ruleId !== null) {
            console.log('Dispatching matchingRule to Redux:', ruleData);
            console.log('Rule data to dispatch:', {
              ruleId: ruleData.ruleId,
              availableTerms: ruleData.availableTerms,
              hasAvailableTerms: !!ruleData.availableTerms,
              availableTermsLength: ruleData.availableTerms?.length,
              minDownPmt: ruleData.minDownPmt,
              minDownPmtType: ruleData.minDownPmtType,
              optimalDownPmt: ruleData.optimalDownPmt,
              optimalDownPmtType: ruleData.optimalDownPmtType,
              optimalTerm: ruleData.optimalTerm
            });
            dispatch(setMatchingRule(ruleData));
          } else {
            console.error('Evaluate Rule API: Invalid response structure', matchingRuleRes);
            console.error('Rule data extracted:', ruleData);
            // Ensure loading state is cleared even on error
            setIsLoadingRule(false);
          }
        } catch (error) {
          console.error('Error calling rule API:', error);
        } finally {
          setIsLoadingRule(false);
        }
      }
    })();
  }, [encounterId, authToken, patientId, fetchedEncounter, dispatch]);

  // Set default values from optimal plan when on custom route (only once per route)
  useEffect(() => {
    const currentRoute = `${programType}-${isCustomResults}`;
    
    // Only run once per route change
    if (defaultsSetForRouteRef.current === currentRoute) {
      return;
    }
    
    if (programType === 'custom' && !isCustomResults) {
      // Reset showFields when on custom route to always show prompt
      setShowFields(false);
      // Set default values from optimal plan (only once per route)
      // Access matchingRule and pfrAmt from closure, don't add to dependencies
      const currentOptDownPayment = matchingRule?.optimalDownPmtType === '%'
        ? (matchingRule.optimalDownPmt || 0) * pfrAmt / 100
        : (matchingRule?.optimalDownPmt || 0);
      
      if (currentOptDownPayment > 0) {
        setCustomDownPayment(currentOptDownPayment.toFixed(2));
      }
      if (matchingRule?.optimalTerm) {
        setCustomTerm(matchingRule.optimalTerm.toString());
      }
      const optimalTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      const optimalRecurringAmt = optimalTermNum > 0 && pfrAmt > currentOptDownPayment
        ? (pfrAmt - currentOptDownPayment) / optimalTermNum
        : 0;
      if (optimalRecurringAmt > 0) {
        setCustomRecurringAmount(optimalRecurringAmt.toFixed(2));
      }
      defaultsSetForRouteRef.current = currentRoute;
    } else if (isCustomResults) {
      setShowFields(true);
      defaultsSetForRouteRef.current = currentRoute;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programType, isCustomResults]); // Only depend on route changes, not field values or matchingRule

  // State for tracking program creation loading
  const [isCreatingProgram, setIsCreatingProgram] = useState(false);

  // Helper function to create program via API and navigate to payment hub
  const createProgramAndNavigate = async (programData: {
    isPaidInFull: boolean;
    downPmtAmt: number;
    isHighRisk?: boolean;
    // Schedule data for payment plans (not needed for full pay)
    terms?: number;
    recurringAmt?: number;
  }) => {
    const clientId = activeEncounter?.clientId;
    const facilityId = activeEncounter?.facilityId;

    if (!clientId || !facilityId) {
      dispatch(showErrorStatus('Client ID and Facility ID are required to create a program'));
      return;
    }

    setIsCreatingProgram(true);

    try {
      // Build schedule request if has terms (for payment plans)
      // Note: pfrPendingBalance and pfrCapturedBalance should equal pfrAmt on creation since no payment has been taken yet
      const scheduleRequest = programData.terms && programData.terms > 0
        ? {
            // TODO: Uncomment when POST program endpoint supports these fields
            // patientId: patientId || 0,
            // patientEncounterId: encounterId || 0,
            paymentFreq: 'M',
            pfrPendingBalance: pfrAmt || 0,
            pfrCapturedBalance: pfrAmt || 0,
            pfrAmt: pfrAmt || 0,
            pfrBalance: pfrAmt || 0,
            terms: programData.terms,
            remainingTerms: programData.terms,
            paymentDueAmt: programData.recurringAmt || 0,
          }
        : undefined;

      // Note: isPaidInFull should always be false on program creation - it gets set to true after full payment is captured
      const programToCreate: ProgramRequest = {
        clientId,
        facilityId,
        patientId: patientId || 0,
        patientEncounterId: encounterId || 0,
        isHighRisk: programData.isHighRisk ?? false,
        isPaidInFull: false,
        programPfrAmt: pfrAmt || 0,
        downPmtAmt: programData.downPmtAmt,
        isDownPmtAuthorized: false,
        isDownPmtCaptured: false,
        isAch: false,
        isAchVerified: false,
        areDocsSigned: false,
        ...(scheduleRequest && { patientPaymentSchedule: scheduleRequest }),
      };

      const createProgramUrl = `${configSettings.unified_aws_app_gateway_uri}/payments/v3/program`;
      console.log('Creating program:', { url: createProgramUrl, program: programToCreate });

      const response = await axios.post(createProgramUrl, programToCreate, {
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && {
            'Authorization': `Bearer ${authToken}`,
            'x-cog-id': authToken,
          }),
        },
      });

      if (response.status === 200 || response.status === 201) {
        const createdProgram = response.data;
        const programId = createdProgram.patientPaymentProgramId;
        console.log('Program created successfully:', createdProgram);

        // Navigate to payment hub with programId and isPaidInFull flag
        const basePath = isMobile 
          ? `/mobile/patient/${patientId}/ioc/${encounterId}/pay`
          : `/hub/patient/${patientId}/ioc/${encounterId}/pay`;
        
        navigate(basePath, { 
          state: { 
            isProgramSetup: true,
            programId,
            isPaidInFull: programData.isPaidInFull,
          } 
        });
      } else {
        dispatch(showErrorStatus('Failed to create program'));
      }
    } catch (error: any) {
      console.error('Error creating program:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create program';
      dispatch(showErrorStatus(errorMessage));
    } finally {
      setIsCreatingProgram(false);
    }
  };

  const handleSelectFullPay = () => {
    createProgramAndNavigate({
      isPaidInFull: true,
      downPmtAmt: pfrAmt || 0,
      isHighRisk: false,
      // No schedule for full pay
    });
  }

  const handleBack = () => {
    // From custom-results route, go back to custom page
    if (isCustomResults) {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`));
      return;
    }
    
    const currentType = programType || 'full';
    // From custom view, go to preauthorized if user has permission, otherwise go to optimal
    if (currentType === 'custom') {
      if (hasPreauthorizedPermission) {
        navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/preauthorized`));
      } else {
        navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/optimal`));
      }
    } 
    // From optimal view, go to preauthorized if user has permission, otherwise go to full
    else if (currentType === 'optimal') {
      if (hasPreauthorizedPermission) {
        navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/preauthorized`));
      } else {
        navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`));
      }
    }
    // From preauthorized view, go to full
    else if (currentType === 'preauthorized') {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`));
    }
    // Default fallback
    else {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`));
    }
  }

  const handleOtherPrograms = () => {
    // Route to preauthorized if user has permission, otherwise route to optimal
    if (hasPreauthorizedPermission) {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/preauthorized`));
    } else {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/optimal`));
    }
  }
  
  const handleSelectPreauthorized = (downPaymentAmt?: number) => {
    const downPmt = downPaymentAmt ?? optDownPayment ?? 0;
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: downPmt,
      isHighRisk: false,
      terms: optimalTermNum,
      recurringAmt: optimalTermNum > 0 && pfrAmt > downPmt ? (pfrAmt - downPmt) / optimalTermNum : 0,
    });
  }

  const handleSelectProgram = () => {
    // Optimal program - use optimal terms and recurring amount
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: optDownPayment ?? 0,
      isHighRisk: false,
      terms: optimalTermNum,
      recurringAmt: recurringAmt,
    });
  }

  const handleSelectOther = () => {
    // If mobile and on optimal program, navigate to no-payment-programs page
    const currentType = isCustomResults ? 'custom' : (programType || 'full');
    if (isMobile && currentType === 'optimal') {
      navigate(`/mobile/patient/${patientId}/ioc/${encounterId}/no-payment-programs`);
    } else {
      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`));
    }
  }

  const handleSelectCustomProgram = () => {
    // Calculate custom down payment for this function
    const calculatedCustomDownPmt = showDownPayment && customDownPayment 
      ? parseFloat(customDownPayment) || 0 
      : optDownPayment || 0;
    
    // Calculate custom recurring amount: use entered value or calculate from term
    const customRecurringAmtValue = showRecurringAmount && customRecurringAmount
      ? parseFloat(customRecurringAmount) || 0
      : 0;
    
    // Calculate term from recurring amount if needed
    let termForProgram = parseFloat(customTerm) || 0;
    if (termForProgram === 0 && customRecurringAmtValue > 0 && pfrAmt > calculatedCustomDownPmt) {
      termForProgram = Math.ceil((pfrAmt - calculatedCustomDownPmt) / customRecurringAmtValue);
    }
    
    // Calculate final recurring amount
    const recurringAmtForProgram = customRecurringAmtValue > 0 
      ? customRecurringAmtValue 
      : (termForProgram > 0 && pfrAmt > calculatedCustomDownPmt ? (pfrAmt - calculatedCustomDownPmt) / termForProgram : 0);
    
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: calculatedCustomDownPmt,
      isHighRisk: isHighRisk,
      terms: termForProgram,
      recurringAmt: recurringAmtForProgram,
    });
  }


  const FullPaymentOption = () => (
    <Grid item xs={12}>
      <Grid container className="mt-4">
      <Typography variant="body2" textAlign="left" sx={{ mb: 5, fontSize: '2rem', lineHeight: '2.6rem' }}>Your first option is 
        the <Box component="span" sx={{ fontWeight: 'bold' }}>Single Pay Program</Box> where you make a single payment
today for the full estimated amount owed. This is a great option because it allows you to get it
out of the way and focus on your health. <Box component="span" sx={{ fontWeight: 'bold' }}>Does this option work for you?</Box></Typography>

        <Grid container direction="row" className="table-header">
          <Grid item xs={6} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="center">Total Estimate</Typography>
          </Grid>
          <Grid item xs={6} className="mt-2 pr-2">
            <Typography variant="subtitle2" textAlign="center">Balance</Typography>
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={6} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="center">{formatNumberToUSD(pfrAmt)}</Typography>
          </Grid>
          <Grid item xs={6} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="center">{formatNumberToUSD(pfrAmt)}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1} alignItems="center">
        {currentProgramType !== 'full' && (
          <Grid item xs={12} md={2} className="mt-2">
            <MUIButton 
              onClick={handleBack} 
              variant="outlined"
              fullWidth
              sx={{ 
                backgroundColor: 'white',
                borderColor: '#e0e0e0',
                color: '#000',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#bdbdbd'
                }
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
            </MUIButton>
          </Grid>
        )}
        <Grid item xs={12} md={currentProgramType !== 'full' ? 5 : 6} className="mt-2">
          <Button onClick={handleSelectFullPay} fullWidth>
            Add Payment Details
          </Button>
        </Grid>
        {matchingRule?.ruleId !== 0 && (
          <Grid item xs={12} md={currentProgramType !== 'full' ? 5 : 6} className="mt-2">
            <MUIButton onClick={handleOtherPrograms} fullWidth variant="text" sx={{ textTransform: 'none' }}>
              Can't afford?
            </MUIButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  )

  const PreauthorizedPaymentProgram = () => {
    const [selectedProgram, setSelectedProgram] = useState<string>('');
    
    // Get matchingRule directly from Redux inside this component to ensure we have the latest value
    const currentMatchingRule = useSelector((state: RootState) => (state?.patientContext?.matchingRule));
    const currentIsLoadingEvaluateRule = useSelector((state: RootState) => (state?.patientContext?.isLoading?.evaluateRule));
    
    // Build payment program options from matchingRule
    const buildProgramOptions = () => {
      console.log('PreauthorizedPaymentProgram: buildProgramOptions called', { matchingRule: currentMatchingRule, pfrAmt });
      
      if (!currentMatchingRule || !pfrAmt || pfrAmt === 0) {
        console.log('PreauthorizedPaymentProgram: Missing matchingRule or pfrAmt', { matchingRule: currentMatchingRule, pfrAmt });
        return [];
      }
      
      // Check if availableTerms exists and is not empty
      if (!currentMatchingRule.availableTerms || currentMatchingRule.availableTerms.trim() === '') {
        console.log('PreauthorizedPaymentProgram: No availableTerms in matchingRule', {
          matchingRule: currentMatchingRule,
          availableTerms: currentMatchingRule.availableTerms,
          ruleId: currentMatchingRule.ruleId
        });
        return [];
      }
      
      const terms = currentMatchingRule.availableTerms.split(',').map(t => t.trim()).filter(t => t);
      if (terms.length === 0) {
        console.log('PreauthorizedPaymentProgram: No valid terms found', currentMatchingRule.availableTerms);
        return [];
      }
      
      // Calculate minimum and optimal down payments from rule API response
      const minimumDownPmt = currentMatchingRule.minDownPmtType === '%'
        ? (currentMatchingRule.minDownPmt || 0) / 100
        : (currentMatchingRule.minDownPmt || 0);
      const optimalDownPmt = currentMatchingRule.optimalDownPmtType === '%'
        ? (currentMatchingRule.optimalDownPmt || 0) / 100
        : (currentMatchingRule.optimalDownPmt || 0);
      
      const options: Array<{
        id: string;
        term: number;
        downPaymentType: 'Min' | 'Opt';
        downPayment: number;
        recurring: number;
        isOptimal: boolean;
      }> = [];
      
      terms.forEach((term: string, index: number) => {
        const termNum = parseInt(term);
        if (isNaN(termNum) || termNum <= 0) {
          console.log('PreauthorizedPaymentProgram: Invalid term', term);
          return;
        }
        
        // Min down payment option - use rule API response data
        const minDownPmt = currentMatchingRule.minDownPmtType === '%'
          ? calculateDownByPercent(minimumDownPmt, pfrAmt)
          : (minimumDownPmt || 0);
        const minRecurring = calculateTermPmt(pfrAmt, minDownPmt, termNum);
        options.push({
          id: `min-${index}`,
          term: termNum,
          downPaymentType: 'Min',
          downPayment: minDownPmt,
          recurring: minRecurring,
          isOptimal: false,
        });
        
        // Optimal down payment option - use rule API response data
        const optDownPmt = currentMatchingRule.optimalDownPmtType === '%'
          ? calculateDownByPercent(optimalDownPmt, pfrAmt)
          : (optimalDownPmt || 0);
        const optRecurring = calculateTermPmt(pfrAmt, optDownPmt, termNum);
        const optimalTermNum = currentMatchingRule.optimalTerm ? parseInt(currentMatchingRule.optimalTerm, 10) : 0;
        const isOptimal = optimalTermNum === termNum;
        options.push({
          id: `opt-${index}`,
          term: termNum,
          downPaymentType: 'Opt',
          downPayment: optDownPmt,
          recurring: optRecurring,
          isOptimal: isOptimal,
        });
      });
      
      console.log('PreauthorizedPaymentProgram: Built options', { options, matchingRule: currentMatchingRule, pfrAmt });
      return options;
    };
    
    const programOptions = buildProgramOptions();
    
    const handlePayWithPlan = () => {
      if (!selectedProgram) {
        dispatch(showErrorStatus('Please select a payment program option'));
        return;
      }
      // Find the selected program option to get the down payment amount
      const selectedOption = programOptions.find(opt => opt.id === selectedProgram);
      const downPaymentAmt = selectedOption?.downPayment ?? optDownPayment ?? 0;
      handleSelectPreauthorized(downPaymentAmt);
    };
    
    // Log matchingRule whenever it changes
    useEffect(() => {
      console.log('PreauthorizedPaymentProgram: matchingRule updated', {
        matchingRule: currentMatchingRule,
        ruleId: currentMatchingRule?.ruleId,
        availableTerms: currentMatchingRule?.availableTerms,
        minDownPmt: currentMatchingRule?.minDownPmt,
        optimalDownPmt: currentMatchingRule?.optimalDownPmt,
        optimalTerm: currentMatchingRule?.optimalTerm,
        clientId: currentMatchingRule?.clientId,
        facilityId: currentMatchingRule?.facilityId,
        fullMatchingRule: currentMatchingRule
      });
    }, [currentMatchingRule]);
    
    // Check if rule is valid - similar to preauthorized-view.tsx
    // It checks for ruleId !== -1, pfrAmt > 0, and optimalTerm exists
    // Also check if we have valid program options
    // Note: ruleId can be 0 or positive, -1 means no rule found
    const isRuleValid = currentMatchingRule && 
      currentMatchingRule.ruleId !== undefined &&
      currentMatchingRule.ruleId !== null &&
      currentMatchingRule.ruleId > 0 && // Changed from !== -1 to > 0 to allow any positive ruleId
      pfrAmt > 0 && 
      currentMatchingRule.optimalTerm && 
      currentMatchingRule.availableTerms &&
      currentMatchingRule.availableTerms.trim() !== '' &&
      programOptions.length > 0;
    
    console.log('PreauthorizedPaymentProgram: Validation check', {
      isRuleValid,
      isLoadingEvaluateRule: currentIsLoadingEvaluateRule,
      isLoadingEncounter,
      ruleId: currentMatchingRule?.ruleId,
      ruleIdCheck: currentMatchingRule?.ruleId !== undefined && currentMatchingRule?.ruleId !== null && currentMatchingRule?.ruleId > 0,
      pfrAmt,
      optimalTerm: currentMatchingRule?.optimalTerm,
      availableTerms: currentMatchingRule?.availableTerms,
      availableTermsCheck: currentMatchingRule?.availableTerms && currentMatchingRule?.availableTerms.trim() !== '',
      programOptionsLength: programOptions.length,
      currentMatchingRule
    });
    
    // Wait for API calls to complete before rendering
    const isLoading = isLoadingEncounter || currentIsLoadingEvaluateRule;
    
    return (
      <Grid item xs={12}>
        <Grid container className="mt-4" spacing={2}>
          {isLoading ? (
            <Grid item xs={12}>
              <Typography variant="body1" textAlign="center">
                Loading payment program options...
              </Typography>
            </Grid>
          ) : isRuleValid ? (
            <>
              {/* Header */}
              <Grid container direction="row" sx={{ borderBottom: '1px solid #e0e0e0', pb: 1, mb: 2 }}>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" fontWeight="bold">Terms</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="subtitle2" fontWeight="bold">Total Down Payment</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="subtitle2" fontWeight="bold">Recurring</Typography>
                </Grid>
              </Grid>
              
              {/* Program Options */}
              <Grid item xs={12}>
                <RadioGroup
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                >
                  {programOptions.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<Radio />}
                      label={
                        <Box
                          sx={{
                            border: option.isOptimal ? '2px solid #4caf50' : '1px solid #e0e0e0',
                            borderRadius: 1,
                            p: 1.5,
                            width: '100%',
                            backgroundColor: option.isOptimal ? '#f1f8f4' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Grid container direction="row" alignItems="center">
                            <Grid item xs={2}>
                              <Typography variant="body1">{option.term}</Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography variant="body1">
                                {option.downPaymentType} {formatNumberToUSD(option.downPayment)}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography variant="body1">{formatNumberToUSD(option.recurring)}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      }
                      sx={{ 
                        m: 0, 
                        mb: 1,
                        width: '100%',
                        '& .MuiFormControlLabel-label': {
                          width: '100%',
                          ml: 1,
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              </Grid>
              
              {/* Action Buttons */}
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={2} className="mt-2">
                  <MUIButton 
                    onClick={handleBack} 
                    variant="outlined"
                    fullWidth
                    sx={{ 
                      backgroundColor: 'white',
                      borderColor: '#e0e0e0',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#bdbdbd'
                      }
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
                  </MUIButton>
                </Grid>
                <Grid item xs={12} md={5} className="mt-2">
                  <Button
                    onClick={handlePayWithPlan}
                    fullWidth
                  >
                    Add Payment Details
                  </Button>
                </Grid>
                <Grid item xs={12} md={5} className="mt-2">
                  <MUIButton onClick={handleSelectOther} fullWidth variant="text" sx={{ textTransform: 'none' }}>
                    Can't afford?
                  </MUIButton>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" textAlign="center">
                No Preauthorized Payment Options exist for Payment Program and Values Entered
              </Typography>
              {currentMatchingRule && (
                <Typography variant="body2" textAlign="center" sx={{ mt: 1, color: 'text.secondary' }}>
                  Debug: ruleId={currentMatchingRule.ruleId}, availableTerms={currentMatchingRule.availableTerms || 'none'}, pfrAmt={pfrAmt}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }

  const OptimalPaymentProgram = () => {
    // Wait for rule API to complete before showing values
    const isLoading = isLoadingEncounter || isLoadingRule;
    // Check if we have valid rule data (ruleId can be 0, but not undefined, null, or -1)
    // -1 is the initial state, so we know the API hasn't returned data yet
    const hasRuleData = matchingRule && 
      matchingRule.ruleId !== undefined && 
      matchingRule.ruleId !== null && 
      matchingRule.ruleId !== -1;
    
    // Debug logging
    console.log('OptimalPaymentProgram render check:', {
      isLoadingEncounter,
      isLoadingRule,
      isLoading,
      hasRuleData,
      matchingRule: matchingRule ? {
        ruleId: matchingRule.ruleId,
        optimalTerm: matchingRule.optimalTerm,
        optimalDownPmt: matchingRule.optimalDownPmt,
        optimalDownPmtType: matchingRule.optimalDownPmtType
      } : null
    });
    
    if (isLoading || !hasRuleData) {
      return (
        <Grid item xs={12}>
          <Grid container className="mt-4" justifyContent="center">
            <Grid item xs={12}>
              <Typography variant="body1" textAlign="center">
                Loading payment program options...
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    }
    
    return (
      <Grid item xs={12}>
        <Grid container className="mt-4">
        <Typography variant="body2" textAlign="left" sx={{ mb: 5, fontSize: '2rem', lineHeight: '2.6rem' }}>No problem, your second option
            is an interest free installment program.
          Many people I speak with choose this automated program because it is a low cost way to spread your payments over time. 
          <br /><br />
Most people in your situation, pick the <Box component="span" sx={{ fontWeight: 'bold' }}>{optimalTermNum} {optimalTermNum > 1 ? 'month' : 'months'}</Box> program
            with a down payment of {matchingRule?.optimalDownPmtType === '%' ? `${matchingRule?.optimalDownPmt || 0}%` : formatNumberToUSD(optDownPayment)}.
            In your case, you would make a down payment today of <Box component="span" sx={{ fontWeight: 'bold' }}>{formatNumberToUSD(optDownPayment)}</Box>, of
            which you've already paid <Box component="span" sx={{ fontWeight: 'bold' }}>{formatNumberToUSD(pfrCollected)}</Box>. The
            remaining payments would be spread over {optimalTermNum} {optimalTermNum > 1 ? 'months' : 'month'} at <Box component="span" sx={{ fontWeight: 'bold' }}>{formatNumberToUSD(recurringAmt)}</Box> per month. <Box component="span" sx={{ fontWeight: 'bold' }}>Does this option work for you?</Box>
</Typography>
        <Grid container direction="row" className="table-header">
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="left">Down Payment</Typography>
          </Grid>
          <Grid item xs={4} className="mt-2">
            <Typography variant="subtitle2" textAlign="center">Term</Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pr-2">
            <Typography variant="subtitle2" textAlign="right">Recurring Amount</Typography>
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="left">{formatNumberToUSD(optDownPayment)}</Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="center">
              {optimalTermNum} {optimalTermNum > 1 ? 'months' : 'month'}
            </Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="right">{formatNumberToUSD(recurringAmt)}</Typography>
          </Grid>
        </Grid>
      </Grid>
      {isMobile && (
        <Grid container direction="row" justifyContent="center">
          <Typography variant="subtitle2" className={classes.fullWidth} textAlign="center">
            The first recurring payment will occur on {moment().add(1, 'months').format('MM/DD/YYYY')} and each month thereafter until the balance is paid in full
          </Typography>
        </Grid>
      )}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={2} className="mt-2">
          <MUIButton 
            onClick={handleBack} 
            variant="outlined"
            fullWidth
            sx={{ 
              backgroundColor: 'white',
              borderColor: '#e0e0e0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd'
              }
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
          </MUIButton>
        </Grid>
        <Grid item xs={12} md={5} className="mt-2">
          <Button onClick={handleSelectProgram} fullWidth>
            Add Payment Details
          </Button>
        </Grid>
        <Grid item xs={12} md={5} className="mt-2">
          <MUIButton onClick={handleSelectOther} fullWidth variant="text" sx={{ textTransform: 'none' }} >
            Can't afford?
          </MUIButton>
        </Grid>
      </Grid>
      </Grid>
    );
  }

  // Memoize onChange handlers to prevent recreation on every render
  const handleDownPaymentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDownPayment(e.target.value);
    // Clear error when user starts typing
    if (downPaymentError) {
      setDownPaymentError('');
    }
  }, [downPaymentError]);

  const handleRecurringAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomRecurringAmount(e.target.value);
    // Clear error when user starts typing
    if (recurringAmountError) {
      setRecurringAmountError('');
    }
  }, [recurringAmountError]);

  // Validate on blur and recalculate program
  const handleDownPaymentBlur = useCallback(() => {
    let finalValue = parseFloat(customDownPayment);
    if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setDownPaymentError(`Down payment cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      finalValue = pfrAmt;
      setCustomDownPayment(pfrAmt.toFixed(2));
    } else {
      setDownPaymentError('');
    }
    
    // Recalculate term if we have recurring amount
    if (showRecurringAmount && customRecurringAmount) {
      const recurringAmt = parseFloat(customRecurringAmount) || 0;
      if (recurringAmt > 0 && pfrAmt > finalValue) {
        const newTerm = Math.ceil((pfrAmt - finalValue) / recurringAmt);
        setCustomTerm(newTerm.toString());
      }
    }
  }, [customDownPayment, customRecurringAmount, showRecurringAmount, pfrAmt]);

  const handleRecurringAmountBlur = useCallback(() => {
    let finalValue = parseFloat(customRecurringAmount);
    if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setRecurringAmountError(`Recurring amount cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      finalValue = pfrAmt;
      setCustomRecurringAmount(pfrAmt.toFixed(2));
    } else {
      setRecurringAmountError('');
    }
    
    // Recalculate term from down payment and recurring amount - use rule API response data
    const currentOptDownPayment = matchingRule?.optimalDownPmtType === '%'
      ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt / 100)
      : (matchingRule?.optimalDownPmt || 0);
    const downPmt = showDownPayment && customDownPayment 
      ? parseFloat(customDownPayment) || 0 
      : currentOptDownPayment || 0;
    
    if (finalValue > 0 && pfrAmt > downPmt) {
      const newTerm = Math.ceil((pfrAmt - downPmt) / finalValue);
      setCustomTerm(newTerm.toString());
    }
  }, [customRecurringAmount, customDownPayment, showDownPayment, matchingRule, pfrAmt]);

  // Calculate values for custom program (moved outside component to prevent recreation)
  const customDownPmt = showDownPayment && customDownPayment 
    ? parseFloat(customDownPayment) || 0 
    : optDownPayment || 0;
  
  const customTermValue = parseFloat(customTerm) || 0;
  const customRecurringAmtValue = showRecurringAmount && customRecurringAmount
    ? parseFloat(customRecurringAmount) || 0
    : 0;
  
  // If term is not set but we have recurring amount, calculate term
  let calculatedTerm = customTermValue;
  if (calculatedTerm === 0 && customRecurringAmtValue > 0 && pfrAmt > customDownPmt) {
    calculatedTerm = Math.ceil((pfrAmt - customDownPmt) / customRecurringAmtValue);
  }
  
  // Adjust calculatedTerm to match the nearest available term (equal or next highest) from matchingRule
  if (calculatedTerm > 0 && matchingRule && matchingRule.ruleId > 0 && matchingRule.ruleId !== -1 && matchingRule.availableTerms) {
    const availableTerms = matchingRule.availableTerms.replace(" ", "").split(",").map((termAsString) => +termAsString).filter(term => !isNaN(term) && term > 0);
    if (availableTerms.length > 0) {
      // Sort available terms in ascending order
      availableTerms.sort((a, b) => a - b);
      const largestTerm = availableTerms[availableTerms.length - 1];
      // Find the term that is equal to or next highest after calculatedTerm
      const bestTerm = availableTerms.find(term => term >= calculatedTerm);
      
      if (bestTerm) {
        calculatedTerm = bestTerm;
      } else {
        // No higher term exists, add terms in intervals of 3
        // Calculate the next term in intervals of 3 from the largest available term
        // Formula: largestTerm + (number of intervals of 3 needed) * 3
        const intervalsNeeded = Math.ceil((calculatedTerm - largestTerm) / 3);
        calculatedTerm = largestTerm + intervalsNeeded * 3;
      }
    }
  }
  
  // Use custom recurring amount if entered, otherwise calculate from down payment and term
  const customRecurringAmtFinal = showRecurringAmount && customRecurringAmount
    ? parseFloat(customRecurringAmount) || 0
    : (calculatedTerm > 0 && pfrAmt > customDownPmt ? (pfrAmt - customDownPmt) / calculatedTerm : 0);

  // Calculate high risk status based on formula from custom-view.tsx
  useEffect(() => {
    if (calculatedTerm > 0 && matchingRule && matchingRule.ruleId > 0 && matchingRule.ruleId !== -1) {
      const availableTerms = matchingRule?.availableTerms?.length > 0 
        ? matchingRule.availableTerms.replace(" ", "").split(",").map((termAsString) => +termAsString) 
        : [];
      const largestTerm = availableTerms.length > 0 ? Math.max(...availableTerms) : undefined;
      // Use rule API response data for minimum down payment
      const minDownPayment = matchingRule.minDownPmt || 0;

      if (largestTerm && minDownPayment !== undefined) {
        let calcDownPayment = minDownPayment;
        if (matchingRule.minDownPmtType === '%') {
          // minDownPayment is stored as percentage (e.g., 20 for 20%), convert to decimal and calculate from PFR amount
          // calculateDownByPercent expects decimal (0.2 for 20%), so divide by 100
          const minDownPaymentDecimal = minDownPayment / 100;
          calcDownPayment = calculateDownByPercent(minDownPaymentDecimal, pfrAmt);
        }
        // Check if we're using an extended term (beyond available terms)
        const isExtendedTerm = calculatedTerm > largestTerm;
        // Use the calculatedTerm directly (already calculated as Math.ceil((pfrAmt - customDownPmt) / customRecurringAmtValue))
        // Always set high risk if using extended term (beyond available terms) or down payment < minimum
        // Note: If term is in available terms (including largest), it's only high risk if down payment is below minimum
        const highRisk = isExtendedTerm || (customDownPmt < calcDownPayment);
        setIsHighRisk(highRisk);
      } else {
        setIsHighRisk(true);
      }
    } else if (calculatedTerm > 0 && (!matchingRule || matchingRule.ruleId === 0 || matchingRule.ruleId === -1)) {
      // If no matching rule, always high risk
      setIsHighRisk(true);
    } else {
      setIsHighRisk(false);
    }
  }, [calculatedTerm, customDownPmt, matchingRule, pfrAmt]);

  const CustomPaymentProgram = useMemo(() => {
    return (
      <Grid item xs={12}>
        {/* Prompt to select which fields to show - always show on custom route, hide on custom-results */}
        {!isCustomResults && programType !== 'hre-approval' && (
          <Grid container className="mt-4" spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" textAlign="left" sx={{ mb: 1, fontSize: '2rem', lineHeight: '2.6rem' }}>
                No problem, is it the down payment, the monthly payment or both that doesn't work
                for you?<br /><br />
                <Box component="span" sx={{ fontStyle: 'italic' }}>Select all that apply.</Box>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ mt: -1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showDownPayment}
                    onChange={(e) => setShowDownPayment(e.target.checked)}
                    test-id="show-down-payment-checkbox"
                  />
                }
                label="Down Payment"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: -1, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showRecurringAmount}
                    onChange={(e) => setShowRecurringAmount(e.target.checked)}
                    test-id="show-recurring-amount-checkbox"
                  />
                }
                label="Recurring Payment"
              />
            </Grid>
          </Grid>
        )}

        {/* Input fields - shown when Next is clicked and at least one checkbox is selected, or on custom-results route */}
        {(showFields || isCustomResults) && (showDownPayment || showRecurringAmount) && programType !== 'hre-approval' && (
          <Grid container className="mt-4" spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" textAlign="left" sx={{ mb: 2, fontSize: '2rem', lineHeight: '2.6rem' }}>
                How close can you get to the {formatNumberToUSD(optDownPayment)} down payment and still feel comfortable?
              </Typography>
            </Grid>
            {showDownPayment && (
              <Grid item xs={12} md={showRecurringAmount ? 6 : 12} key="down-payment-field">
                <Box sx={isCustomResults ? { width: 'fit-content', minWidth: '210px' } : {}}>
                  <TextField
                    label="Remaining Down Payment"
                    name="downPayment"
                    type="number"
                    value={customDownPayment}
                    onChange={handleDownPaymentChange}
                    onBlur={handleDownPaymentBlur}
                    error={!!downPaymentError}
                    helperText={downPaymentError}
                    placeholder="0.00"
                    test-id="custom-down-payment"
                  />
                </Box>
              </Grid>
            )}
            {isCustomResults && showDownPayment && showRecurringAmount && programType !== 'hre-approval' && (
              <Grid item xs={12}>
                <Typography variant="body2" textAlign="left" sx={{ fontSize: '2rem', lineHeight: '2.6rem' }}>How close can you get to the {formatNumberToUSD(recurringAmt)} recurring payment and still feel comfortable?</Typography>
              </Grid>
            )}
            {showRecurringAmount && (
              <Grid item xs={12} md={showDownPayment ? 6 : 12} key="recurring-amount-field">
                <Box sx={isCustomResults ? { width: 'fit-content', minWidth: '210px' } : {}}>
                  <TextField
                    label="Recurring Amount"
                    name="recurringAmount"
                    type="number"
                    value={customRecurringAmount}
                    onChange={handleRecurringAmountChange}
                    onBlur={handleRecurringAmountBlur}
                    error={!!recurringAmountError}
                    helperText={recurringAmountError}
                    placeholder="0.00"
                    test-id="custom-recurring-amount"
                  />
                </Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mt: 3, 
                    fontWeight: 'bold',
                    color: isHighRisk ? 'error.main' : 'inherit'
                  }}
                >
                  {isHighRisk ? 'High Risk Program' : 'Best Preauthorized Program'}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
        {/* Program results - always show on custom-results route, or when fields are selected and term is calculated */}
        {(isCustomResults || ((showFields || isCustomResults) && (showDownPayment || showRecurringAmount) && calculatedTerm > 0)) && programType !== 'hre-approval' && (
          <Grid container className="mt-4">
            <Grid container direction="row" className="table-header">
              <Grid item xs={4} className="mt-2 pl-2">
                <Typography variant="subtitle2" textAlign="left">Down Payment</Typography>
              </Grid>
              <Grid item xs={4} className="mt-2">
                <Typography variant="subtitle2" textAlign="center">Term</Typography>
              </Grid>
              <Grid item xs={4} className="mt-2 pr-2">
                <Typography variant="subtitle2" textAlign="right">Recurring Amount</Typography>
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={4} className="mt-2 pl-2">
                <Typography variant="subtitle2" textAlign="left">{formatNumberToUSD(customDownPmt)}</Typography>
              </Grid>
              <Grid item xs={4} className="mt-2 pl-2">
                <Typography variant="subtitle2" textAlign="center">
                  {calculatedTerm} {calculatedTerm > 1 ? 'months' : 'month'}
                </Typography>
              </Grid>
              <Grid item xs={4} className="mt-2 pl-2">
                <Typography variant="subtitle2" textAlign="right">{formatNumberToUSD(customRecurringAmtFinal)}</Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
        {/* High Risk Exception Approve Button - moved to same line as Back button */}
        {isMobile && (
          <Grid container direction="row" justifyContent="center">
            <Typography variant="subtitle2" className={classes.fullWidth} textAlign="center">
              {(isCustomResults || ((showFields || isCustomResults) && calculatedTerm > 0)) && (
                <>The first recurring payment will occur on {moment().add(1, 'months').format('MM/DD/YYYY')} and each month thereafter until the balance is paid in full</>
              )}
            </Typography>
          </Grid>
        )}
        <Grid container spacing={1} alignItems="center">
          {(showFields || isCustomResults || (programType === 'custom' && !isCustomResults)) && (
            <Grid item xs={12} md={2} className="mt-2">
              <MUIButton 
                onClick={handleBack} 
                variant="outlined"
                fullWidth
                sx={{ 
                  backgroundColor: 'white',
                  borderColor: '#e0e0e0',
                  color: '#000',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#bdbdbd'
                  }
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
              </MUIButton>
            </Grid>
          )}
          {isCustomResults && isHighRisk && calculatedTerm > 0 && (
            <Grid item xs={12} md={10} className="mt-2">
              <Button 
                test-id="approve-high-risk-button"
                fullWidth
                onClick={() => {
                  navigate(`/hub/patient/${patientId}/ioc/${encounterId}/program/hre-approval`);
                }}
              >
                Send HRE
              </Button>
            </Grid>
          )}
          {!(isCustomResults && isHighRisk && calculatedTerm > 0) && (
            <Grid item xs={12} md={(showFields || isCustomResults || (programType === 'custom' && !isCustomResults)) ? 10 : 12} className="mt-2">
              {!showFields && !isCustomResults ? (
                <Button 
                  onClick={() => {
                    if (showDownPayment || showRecurringAmount) {
                      navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom-results`));
                    }
                  }}
                  fullWidth
                  disabled={!showDownPayment && !showRecurringAmount}
                >
                  Next
                </Button>
              ) : (
                <Button 
                  onClick={handleSelectCustomProgram} 
                  fullWidth
                >
                  {programType === 'hre-approval' && (hreApprovalStatus === 'rejected-by-client' || hreApprovalStatus === 'rejected-by-guarantor')
                    ? 'Save'
                    : 'Add Payment Details'}
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }, [isCustomResults, showDownPayment, showRecurringAmount, showFields, calculatedTerm, customDownPmt, customRecurringAmtFinal, handleDownPaymentChange, handleRecurringAmountChange, classes, patientId, encounterId, navigate, handleBack, handleSelectCustomProgram, getProgramPath, programType, hreApprovalStatus, isHighRisk]);

  // Redirect to full if no programType is specified (but not if on custom-results route)
  useEffect(() => {
    if (!programType && !isCustomResults && patientId > 0 && encounterId > 0) {
      const path = getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/full`);
      navigate(path, { replace: true });
    }
  }, [programType, isCustomResults, patientId, encounterId, navigate, getProgramPath]);

  return (
    <main>
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            {isMobile ? 'Mobile Program' : 'Payment Program'}
          </Typography>
          
          {/* Navigation Radio Button Bar */}
          {!isMobile && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', width: '100%' }}>
              <ToggleButtonGroup
              value={currentProgramType}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  if (newValue === 'custom' && (showDownPayment || showRecurringAmount)) {
                    navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom-results`));
                  } else {
                    navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/${newValue}`));
                  }
                }
              }}
              aria-label="payment program type"
              sx={{
                width: '100%',
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: '1px solid #e0e0e0',
                  color: '#000',
                  backgroundColor: 'white',
                  '&.Mui-selected': {
                    backgroundColor: '#297ec1',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1f5f8f',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              }}
            >
              <ToggleButton value="full" aria-label="single pay">
                SINGLE PAY
              </ToggleButton>
              {hasPreauthorizedPermission ? (
                <ToggleButton value="preauthorized" aria-label="preauthorized payment">
                  Preauthorized
                </ToggleButton>
              ) : (
                <ToggleButton value="optimal" aria-label="optimal payment">
                  Optimal
                </ToggleButton>
              )}
              <ToggleButton value="custom" aria-label="custom payment">
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          )}
          
          <Grid container spacing={2.5}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Total Estimate
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(pfrAmt)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Paid to Date
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(pfrCollected)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Remaining Balance
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(remainingPfr)}
              </Typography>
            </Grid>
            {programType === 'hre-approval' && (
              <>
                <Grid item xs={12}>
                  <RadioGroup
                    value={hreApprovalStatus}
                    onChange={(e) => setHreApprovalStatus(e.target.value)}
                    name="hre-approval-status"
                  >
                    <FormControlLabel value="approved" control={<Radio />} label="Approved" />
                    <FormControlLabel value="approved-with-amendment" control={<Radio />} label="Approved with Amendment" />
                    <FormControlLabel value="rejected-by-client" control={<Radio />} label="Rejected by Client" />
                    <FormControlLabel value="rejected-by-guarantor" control={<Radio />} label="Rejected by Guarantor" />
                  </RadioGroup>
                </Grid>
                {hreApprovalStatus === 'approved-with-amendment' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Down Payment"
                        name="downPayment"
                        type="number"
                        value={customDownPayment}
                        onChange={handleDownPaymentChange}
                        onBlur={handleDownPaymentBlur}
                        error={!!downPaymentError}
                        helperText={downPaymentError}
                        placeholder="0.00"
                        test-id="hre-down-payment"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Recurring Amount"
                        name="recurringAmount"
                        type="number"
                        value={customRecurringAmount}
                        onChange={handleRecurringAmountChange}
                        onBlur={handleRecurringAmountBlur}
                        error={!!recurringAmountError}
                        helperText={recurringAmountError}
                        placeholder="0.00"
                        test-id="hre-recurring-amount"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="HRE Response"
                    name="approvalResponse"
                    value={approvalResponse}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setApprovalResponse(e.target.value)}
                    onBlur={() => {}}
                    multiline
                    rows={4}
                    test-id="approval-response-input"
                    className="w-100"
                  />
                </Grid>
              </>
            )}
            {currentProgramType === 'full'
              ? <FullPaymentOption />
              : currentProgramType === 'preauthorized'
              ? <PreauthorizedPaymentProgram />
              : currentProgramType === 'optimal'
              ? <OptimalPaymentProgram />
              : <>{CustomPaymentProgram}</>
            }
          </Grid>
        </Paper>
      </div>
    </main>
  )
}

export default MobilePaymentProgramModal;

